import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { NewResourceCard } from "@/components/cards/NewResourceCard";
import { PATHS, PATH_LABEL_KEYS } from "@/config/paths";
import { SCHEMA_NAMES } from "instantdb/types";
import { DNDSortContainer } from "@/lib/DNDSortContainer";
import { useMobile } from "@/providers/mobileProvider";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { NotFound } from "@/not-found";
import { GlobalError } from "@/global-error";
import { Container } from "@/components/ui/container";
import { useHealthAreas } from "@/hooks/healthAreas/useHealthAreas";
import { HealthAreaCard } from "@/components/cards/healthAreas/HealthAreaCard";
import { useRouter } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/dashboard/health-areas")({
  component: HealthAreasPage,
  notFoundComponent: NotFound,
  errorComponent: GlobalError,
});

function HealthAreasPage() {
  const router = useRouter();
  const { isMobile } = useMobile();
  const { healthAreas, handleCreateHealthArea, isLoading } = useHealthAreas();
  const { setMobileNavbar } = useMobileNavbar();
  const { t } = useTranslation();

  // Set up navbar configuration
  useEffect(() => {
    if (isMobile) {
      setMobileNavbar({
        title: t(PATH_LABEL_KEYS[PATHS.HEALTH_AREAS]),
        navbarChildren: undefined,
      });
    }
  }, [isMobile, setMobileNavbar]);

  if (isLoading) return null;

  return (
    <Container>
      {!isMobile && (
        <Heading level="h1" size="h4" className="col-span-12 tracking-tight">
          {t(PATH_LABEL_KEYS[PATHS.HEALTH_AREAS])}
        </Heading>
      )}

      <DNDSortContainer
        slug={SCHEMA_NAMES.healthAreas}
        resources={healthAreas}
        createResourceCard={
          <NewResourceCard
            type="healthAreas"
            className="col-span-12 @[768px]:col-span-6 @[1024px]:col-span-4"
            onClick={() => handleCreateHealthArea({})}
          />
        }
        renderOverlay={(healthArea) => (
          <HealthAreaCard key={healthArea.id} healthArea={healthArea} isDragging isOverlay />
        )}
      >
        {(activeId, healthAreas) =>
          healthAreas.map((healthArea) => (
            <HealthAreaCard
              key={healthArea.id}
              healthArea={healthArea}
              isDragging={activeId === healthArea.id}
              isOverlay={false}
            />
          ))
        }
      </DNDSortContainer>

      <div className="col-span-12 md:mt-0 text-center md:text-left">
        <Button
          onClick={() => router.navigate({ to: PATHS.LIBRARY })}
          variant="link"
          className="text-primary hover:text-primary/80 transition-colors"
        >
          {t("health_areas_page.explore_more")} <ChevronRight />
        </Button>
      </div>
    </Container>
  );
}
